import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Home, Users, Heart, Shield, Phone, Menu, X, ChevronRight, MapPin, Mail, Handshake, Building2, UserCheck, Clock, FileText } from "lucide-react";
import { Brochure, Flyer } from "./Brochure";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-walkthrough/artifacts/x82qaf6p_logo_converted_1772814484394.jpeg";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1773227060422-ee506b865417?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzZW5pb3JzJTIwc3VwcG9ydCUyMGdyb3VwfGVufDB8fHx8MTc3NDgzMzQxOXww&ixlib=rb-4.1.0&q=85",
  about: "https://images.pexels.com/photos/164558/pexels-photo-164558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  veterans: "https://images.unsplash.com/photo-1764760214766-17c69378399f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHx2ZXRlcmFuJTIwcG9ydHJhaXQlMjBvdXRkb29yc3xlbnwwfHx8fDE3NzQ4MjU0MjF8MA&ixlib=rb-4.1.0&q=85",
  elderly: "https://images.pexels.com/photos/1653898/pexels-photo-1653898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  sober: "https://images.pexels.com/photos/36624101/pexels-photo-36624101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  reentry: "https://images.unsplash.com/photo-1758599668186-c47df67d025d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBnYXRoZXJpbmclMjBvdXRkb29yc3xlbnwwfHx8fDE3NzQ4MjU0Mzd8MA&ixlib=rb-4.1.0&q=85",
  partner: "https://images.unsplash.com/photo-1745847768380-2caeadbb3b71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxoYW5kc2hha2UlMjBwYXJ0bmVyc2hpcHxlbnwwfHx8fDE3NzQ4MjU0Mzl8MA&ixlib=rb-4.1.0&q=85"
};

// Navigation Component
const Navigation = ({ activeSection, setActiveSection, mobileMenuOpen, setMobileMenuOpen }) => {
  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About Us" },
    { id: "services", label: "Services" },
    { id: "partner", label: "Partner With Us" },
    { id: "apply", label: "Apply" },
    { id: "contact", label: "Contact" }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-[#E6DFD3]/50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveSection("home")} data-testid="logo-link">
            <img src={LOGO_URL} alt="Momentum Rise Enterprise" className="h-24 w-24 object-contain" />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-[#1C1917] leading-tight">Momentum Rise</h1>
              <p className="text-sm font-bold text-[#5C5346]">Enterprise</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`nav-link text-sm font-medium transition-colors ${activeSection === item.id ? 'text-[#800000]' : 'text-[#5C5346] hover:text-[#800000]'}`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <button 
            className="lg:hidden p-2" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#FDFBF7] border-t border-[#E6DFD3]" data-testid="mobile-nav">
          <nav className="flex flex-col py-4">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setMobileMenuOpen(false); }}
                className={`px-6 py-3 text-left font-medium ${activeSection === item.id ? 'text-[#800000] bg-[#F4EFEA]' : 'text-[#5C5346]'}`}
                data-testid={`mobile-nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = ({ setActiveSection }) => (
  <section className="relative min-h-screen flex items-center" data-testid="hero-section">
    <div className="absolute inset-0">
      <img src={IMAGES.hero} alt="Sunrise over field" className="w-full h-full object-cover" />
      <div className="absolute inset-0 hero-overlay"></div>
    </div>
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
      <div className="max-w-3xl">
        <p className="text-sm tracking-widest uppercase text-[#DAA520] font-semibold mb-4">A Place to Rise, Rebuild, and Belong</p>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl text-white font-bold leading-tight mb-6 text-shadow animate-fade-in-up animate-delay-100">
          Rise Together.<br />Live With Purpose.
        </h1>
        <p className="text-xl text-white/90 mb-10 leading-relaxed animate-fade-in-up animate-delay-200">
          Momentum Rise Enterprise provides shared, safe, and structured living for veterans, elderly, individuals in recovery, those re-entering society, and those experiencing homelessness. Serving communities across Georgia.
        </p>
        <div className="flex flex-wrap gap-4 animate-fade-in-up animate-delay-300">
          <button 
            onClick={() => setActiveSection("apply")}
            className="btn-primary px-8 py-4 rounded-full font-semibold text-lg"
            data-testid="hero-apply-btn"
          >
            Apply for Housing
          </button>
          <button 
            onClick={() => setActiveSection("partner")}
            className="btn-outline bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 px-8 py-4 rounded-full font-semibold text-lg"
            data-testid="hero-partner-btn"
          >
            Partner With Us
          </button>
        </div>
      </div>
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section className="py-24 sm:py-32 bg-[#FDFBF7]" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-sm tracking-widest uppercase text-[#800000] font-semibold mb-4">About Us</p>
          <h2 className="text-4xl sm:text-5xl text-[#1C1917] font-bold mb-6">A Place to Rise, Rebuild, and Belong</h2>
          <p className="text-lg text-[#5C5346] leading-relaxed mb-6">
            My name is Lalita Henry, and I am the founder of Momentum Rise Enterprise. I started this business in February 2026 from a deep place in my heart with the vision to create something meaningful for people who deserve more than just a place to stay—they deserve a place to feel safe, respected, and supported.
          </p>
          <p className="text-lg text-[#5C5346] leading-relaxed mb-6">
            I was inspired to start Momentum Rise Enterprise because I saw a need for independent shared living that truly honors people's dignity and their ability to manage their own lives. I wanted to build a home-like environment for adults who may be facing transitions, challenges, or fresh starts—including veterans, re-entry individuals, and seniors.
          </p>
          <p className="text-lg text-[#5C5346] leading-relaxed mb-8">
            I founded this business because I believe in second chances, community, and the power of a stable home. Momentum Rise Enterprise is my way of helping others rise, rebuild, and step into a better future.
          </p>
          <div className="flex items-center gap-4">
            <div className="h-1 w-16 bg-[#DAA520]"></div>
            <p className="text-[#800000] font-semibold italic">"A place to rise, rebuild, and belong."</p>
          </div>
        </div>
        <div className="relative">
          <img src={IMAGES.about} alt="Community home" className="rounded-2xl shadow-xl w-full" />
          <div className="absolute -bottom-6 -left-6 bg-[#800000] text-white p-6 rounded-xl shadow-lg">
            <p className="text-3xl font-bold">2026</p>
            <p className="text-sm opacity-80">Founded</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Veterans Housing",
      description: "Dedicated housing for those who served our country. Structured support for transitioning veterans and those experiencing housing insecurity.",
      image: IMAGES.veterans,
      color: "#800000"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Senior Living",
      description: "Affordable, community-based housing for elderly individuals. Safe environments with the support needed to age with dignity.",
      image: IMAGES.elderly,
      color: "#DAA520"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Sober Living",
      description: "Substance-free environments for individuals committed to recovery. Structured routines and peer support for lasting sobriety.",
      image: IMAGES.sober,
      color: "#2E8B57"
    },
    {
      icon: <UserCheck className="w-8 h-8" />,
      title: "Re-Entry Housing",
      description: "Transitional housing for citizens rebuilding their lives after incarceration. Pathways to independence and community reintegration.",
      image: IMAGES.reentry,
      color: "#800000"
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: "Homeless Services",
      description: "Safe shelter and supportive housing for individuals experiencing homelessness. A pathway from the streets to stability and self-sufficiency.",
      image: IMAGES.hero,
      color: "#DAA520"
    }
  ];

  return (
    <section className="py-24 sm:py-32 bg-[#F4EFEA]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm tracking-widest uppercase text-[#800000] font-semibold mb-4">Our Services</p>
          <h2 className="text-4xl sm:text-5xl text-[#1C1917] font-bold mb-6">Who We Serve</h2>
          <p className="text-lg text-[#5C5346] max-w-2xl mx-auto">We provide specialized housing solutions for five key populations, each with unique needs and pathways to stability.</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div key={index} className="service-card bg-white rounded-2xl overflow-hidden border border-[#E6DFD3]" data-testid={`service-card-${index}`}>
              <div className="h-48 overflow-hidden">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4" style={{ backgroundColor: `${service.color}15`, color: service.color }}>
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-[#1C1917] mb-3">{service.title}</h3>
                <p className="text-[#5C5346] leading-relaxed">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: "5", label: "Populations Served", icon: <Users className="w-6 h-6" /> },
    { number: "24/7", label: "Support Available", icon: <Clock className="w-6 h-6" /> },
    { number: "100%", label: "Commitment to Safety", icon: <Shield className="w-6 h-6" /> },
    { number: "1", label: "Mission: Stability", icon: <Home className="w-6 h-6" /> }
  ];

  return (
    <section className="py-20 bg-[#800000]" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center text-white" data-testid={`stat-${index}`}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 mb-4">
                {stat.icon}
              </div>
              <p className="text-4xl sm:text-5xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stat.number}</p>
              <p className="text-white/80 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Partner Section
const PartnerSection = () => {
  const [formData, setFormData] = useState({
    organization_name: "",
    contact_name: "",
    email: "",
    phone: "",
    organization_type: "",
    partnership_interest: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partners`, formData);
      setSubmitted(true);
      setFormData({
        organization_name: "",
        contact_name: "",
        email: "",
        phone: "",
        organization_type: "",
        partnership_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting partner inquiry:", error);
    }
    setLoading(false);
  };

  const orgTypes = [
    { value: "nonprofit", label: "Nonprofit Organization" },
    { value: "church", label: "Church / Faith-Based" },
    { value: "government", label: "Government Agency" },
    { value: "corporate", label: "Corporate / Business" },
    { value: "other", label: "Other" }
  ];

  const interests = [
    { value: "referrals", label: "Resident Referrals" },
    { value: "services", label: "Wraparound Services" },
    { value: "funding", label: "Funding / Grants" },
    { value: "sponsorship", label: "Sponsorship" },
    { value: "volunteer", label: "Volunteer Support" }
  ];

  return (
    <section className="py-24 sm:py-32 bg-[#F4EFEA]" data-testid="partner-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm tracking-widest uppercase text-[#800000] font-semibold mb-4">Partner With Us</p>
          <h2 className="text-4xl sm:text-5xl text-[#1C1917] font-bold mb-6">Together, We Can Do More</h2>
          <p className="text-lg text-[#5C5346] max-w-2xl mx-auto">We're seeking community partners who share our vision of stable housing for all. Fill out the form below and let's start the conversation.</p>
        </div>
        
        <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-lg border border-[#E6DFD3]">
          {submitted ? (
            <div className="text-center py-12" data-testid="partner-success">
              <div className="w-16 h-16 bg-[#2E8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1C1917] mb-2">Thank You!</h3>
              <p className="text-[#5C5346]">We've received your partnership inquiry and will be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="partner-form">
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Organization Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.organization_name}
                      onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="partner-org-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Your Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="partner-contact-name"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="partner-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="partner-phone"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Organization Type *</label>
                    <select
                      required
                      value={formData.organization_type}
                      onChange={(e) => setFormData({...formData, organization_type: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="partner-org-type"
                    >
                      <option value="">Select type...</option>
                      {orgTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Partnership Interest *</label>
                    <select
                      required
                      value={formData.partnership_interest}
                      onChange={(e) => setFormData({...formData, partnership_interest: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="partner-interest"
                    >
                      <option value="">Select interest...</option>
                      {interests.map(interest => (
                        <option key={interest.value} value={interest.value}>{interest.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1">Message *</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none resize-none"
                    placeholder="Tell us about your organization and how you'd like to partner..."
                    data-testid="partner-message"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-4 rounded-full font-semibold text-lg disabled:opacity-50"
                  data-testid="partner-submit-btn"
                >
                  {loading ? "Submitting..." : "Submit Partnership Inquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Apply Section
const ApplySection = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    population_type: "",
    current_situation: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    additional_info: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, formData);
      setSubmitted(true);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        date_of_birth: "",
        population_type: "",
        current_situation: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        additional_info: ""
      });
    } catch (error) {
      console.error("Error submitting application:", error);
    }
    setLoading(false);
  };

  const populationTypes = [
    { value: "veteran", label: "Veteran" },
    { value: "elderly", label: "Senior (65+)" },
    { value: "sober_recovery", label: "Sober Recovery" },
    { value: "reentry", label: "Re-Entry" },
    { value: "homeless", label: "Experiencing Homelessness" }
  ];

  return (
    <section className="py-24 sm:py-32 bg-[#F4EFEA]" data-testid="apply-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm tracking-widest uppercase text-[#800000] font-semibold mb-4">Apply Now</p>
          <h2 className="text-4xl sm:text-5xl text-[#1C1917] font-bold mb-6">Start Your Journey</h2>
          <p className="text-lg text-[#5C5346] max-w-2xl mx-auto">Take the first step toward stable, supportive housing. Fill out the application below and our team will be in touch.</p>
        </div>
        
        <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-lg border border-[#E6DFD3]">
          {submitted ? (
            <div className="text-center py-12" data-testid="apply-success">
              <div className="w-16 h-16 bg-[#2E8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-[#2E8B57]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1C1917] mb-2">Application Received!</h3>
              <p className="text-[#5C5346]">Thank you for applying. Our team will review your application and contact you within 3-5 business days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="apply-form">
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="apply-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="apply-last-name"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="apply-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="apply-phone"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="apply-dob"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">I am a... *</label>
                    <select
                      required
                      value={formData.population_type}
                      onChange={(e) => setFormData({...formData, population_type: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="apply-population-type"
                    >
                      <option value="">Select category...</option>
                      {populationTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1">Current Situation *</label>
                  <textarea
                    required
                    rows={3}
                    value={formData.current_situation}
                    onChange={(e) => setFormData({...formData, current_situation: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none resize-none"
                    placeholder="Briefly describe your current housing situation..."
                    data-testid="apply-situation"
                  />
                </div>
                
                <div className="border-t border-[#E6DFD3] pt-6">
                  <h4 className="text-lg font-semibold text-[#1C1917] mb-4">Emergency Contact (Optional)</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1">Contact Name</label>
                      <input
                        type="text"
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                        data-testid="apply-emergency-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1">Contact Phone</label>
                      <input
                        type="tel"
                        value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                        data-testid="apply-emergency-phone"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#1C1917] mb-1">Additional Information</label>
                  <textarea
                    rows={3}
                    value={formData.additional_info}
                    onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none resize-none"
                    placeholder="Any additional information you'd like us to know..."
                    data-testid="apply-additional-info"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-4 rounded-full font-semibold text-lg disabled:opacity-50"
                  data-testid="apply-submit-btn"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry_type: "general",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        inquiry_type: "general",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
    }
    setLoading(false);
  };

  return (
    <section className="py-24 sm:py-32 bg-[#FDFBF7]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm tracking-widest uppercase text-[#800000] font-semibold mb-4">Contact Us</p>
            <h2 className="text-4xl sm:text-5xl text-[#1C1917] font-bold mb-6">Get In Touch</h2>
            <p className="text-lg text-[#5C5346] leading-relaxed mb-8">
              Have questions about our housing programs? Want to learn more about how we can help? Reach out to us—we're here to help.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#800000]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1C1917]">Email</h4>
                  <a href="mailto:lalita@risewithmomentum.com" className="text-[#5C5346] hover:text-[#800000]">lalita@risewithmomentum.com</a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#800000]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1C1917]">Phone</h4>
                  <a href="tel:478-294-8226" className="text-[#5C5346] hover:text-[#800000]">(478) 294-8226</a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserCheck className="w-6 h-6 text-[#800000]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1C1917]">Contact</h4>
                  <p className="text-[#5C5346]">Lalita Henry, Founder</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#800000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#800000]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1C1917]">Service Area</h4>
                  <p className="text-[#5C5346]">Serving communities across Georgia</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E6DFD3]">
            {submitted ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-16 h-16 bg-[#2E8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#2E8B57]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1C1917] mb-2">Message Sent!</h3>
                <p className="text-[#5C5346]">Thank you for reaching out. We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="text-2xl font-bold text-[#1C1917] mb-6">Send a Message</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Your Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                      data-testid="contact-name"
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1C1917] mb-1">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1C1917] mb-1">Message *</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#E6DFD3] focus:ring-2 focus:ring-[#800000] focus:border-[#800000] outline-none resize-none"
                      placeholder="How can we help you?"
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary py-4 rounded-full font-semibold text-lg disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-[#1A1A1A] text-[#FDFBF7] py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            <img src={LOGO_URL} alt="Momentum Rise Enterprise" className="h-24 w-24 object-contain" />
            <div>
              <h3 className="text-2xl font-bold">Momentum Rise</h3>
              <p className="text-base font-bold text-[#DAA520]">Enterprise</p>
            </div>
          </div>
          <p className="text-[#FDFBF7]/70 leading-relaxed mb-4 italic">
            "A place to rise, rebuild, and belong."
          </p>
          <p className="text-[#FDFBF7]/70 leading-relaxed mb-6">
            Providing shared, safe, and structured living for veterans, elderly, individuals in recovery, those re-entering society, and those experiencing homelessness. Serving communities across Georgia.
          </p>
          <p className="text-sm text-[#FDFBF7]/50">© 2026 Momentum Rise Enterprise. All rights reserved.</p>
        </div>
        
        <div>
          <h4 className="text-[#DAA520] font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-3">
            <li><a href="#about" className="footer-link">About Us</a></li>
            <li><a href="#services" className="footer-link">Our Services</a></li>
            <li><a href="#partner" className="footer-link">Partner With Us</a></li>
            <li><a href="#apply" className="footer-link">Apply Now</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="text-[#DAA520] font-semibold mb-4">Contact</h4>
          <ul className="space-y-3 text-[#FDFBF7]/70">
            <li className="font-medium text-[#FDFBF7]">Lalita Henry, Founder</li>
            <li><a href="mailto:lalita@risewithmomentum.com" className="hover:text-[#DAA520]">lalita@risewithmomentum.com</a></li>
            <li><a href="tel:478-294-8226" className="hover:text-[#DAA520]">(478) 294-8226</a></li>
            <li>Serving Georgia</li>
          </ul>
        </div>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check for brochure/flyer routes
  const path = window.location.pathname;
  if (path === "/brochure") {
    return <Brochure />;
  }
  if (path === "/flyer") {
    return <Flyer />;
  }

  const renderSection = () => {
    switch(activeSection) {
      case "home":
        return (
          <>
            <HeroSection setActiveSection={setActiveSection} />
            <AboutSection />
            <ServicesSection />
            <StatsSection />
            <PartnerSection />
            <ApplySection />
            <ContactSection />
          </>
        );
      case "about":
        return <AboutSection />;
      case "services":
        return <ServicesSection />;
      case "partner":
        return <PartnerSection />;
      case "apply":
        return <ApplySection />;
      case "contact":
        return <ContactSection />;
      default:
        return <HeroSection setActiveSection={setActiveSection} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="app-container">
      <Navigation 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main className="pt-28">
        {renderSection()}
      </main>
      <Footer />
    </div>
  );
}

export default App;
